"use client";

import { useRef, useState, type ReactNode } from "react";
import { Button } from "@/components/ui/button";
import type { InviteCompose } from "./AuthForm";

/**
 * TA-1324 T7. What an invite hands the owner: the one-time link inside a ready-to-paste email (subject and body), and a
 * button that copies the body. It is the form's success notice in the shared contract (role="status",
 * data-testid="action-notice"), so the everyControl sweep and the e2e specs read it like any other.
 *
 * The copy button always ends in a visible state, never silence:
 * - the clipboard write happens INSIDE the click handler (browsers refuse one made after an await or a timer);
 * - if the clipboard is missing or refuses (an insecure page, a denied permission), the message is selected so one
 *   keypress copies it, and the old copy command is tried; the line says which of those happened;
 * - if even selection is impossible, the line says it could not copy.
 *
 * The link is a credential and is shown here once, in this form's state; nothing stores it (no localStorage, no URL).
 * Tokens only (CLAUDE.md section 12.1): no shadow, no gradient, radii from the scale, off-white and off-black.
 */
export type CopyStatus = "idle" | "copied" | "selected" | "failed";

const STATUS_LINE: Record<CopyStatus, string> = {
  idle: "",
  copied: "Copied",
  selected: "Selected. Press Ctrl+C, or Command+C on a Mac, to copy it.",
  failed: "Could not copy. Select the message and copy it by hand.",
};

/** Select the element's text so one keypress copies it, and try the old copy command. */
export function selectForCopy(el: Element | null): Exclude<CopyStatus, "idle"> {
  if (el === null || typeof window === "undefined" || typeof document === "undefined") return "failed";
  const selection = window.getSelection();
  if (selection === null) return "failed";
  const range = document.createRange();
  range.selectNodeContents(el);
  selection.removeAllRanges();
  selection.addRange(range);
  try {
    return document.execCommand("copy") ? "copied" : "selected";
  } catch {
    return "selected";
  }
}

/**
 * The body as text, with each occurrence of the address and the link wrapped in a span marked data-user-content. Those two
 * are the only parts that are not our own copy; the mark is what the design lint skips, so it must not sit on the whole
 * notice. The text content is unchanged, so selecting and copying the element still yields the body exactly.
 */
function userContentSpans(body: string, values: readonly string[]): ReactNode[] {
  const wanted = values.filter((v) => v !== "");
  if (wanted.length === 0) return [body];
  const pattern = new RegExp(`(${wanted.map((v) => v.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")).join("|")})`);
  return body.split(pattern).map((part, i) =>
    wanted.includes(part) ? (
      <span key={i} data-user-content>
        {part}
      </span>
    ) : (
      part
    ),
  );
}

export function InviteMessage({ compose }: { compose: InviteCompose }) {
  const [status, setStatus] = useState<CopyStatus>("idle");
  const bodyRef = useRef<HTMLPreElement>(null);

  const copy = (): void => {
    const clipboard = typeof navigator === "undefined" ? undefined : navigator.clipboard;
    // Started in this same tick, inside the click.
    const written = clipboard ? clipboard.writeText(compose.body) : Promise.reject(new Error("no clipboard"));
    written.then(
      () => setStatus("copied"),
      () => setStatus(selectForCopy(bodyRef.current)),
    );
  };

  return (
    <div role="status" data-testid="action-notice" className="flex flex-col gap-3 text-[length:var(--text-sm)] text-[var(--color-ink)]">
      <p>
        Send this one-time link to <span data-user-content>{compose.email}</span>. It is shown only now. Paste the message below into your own mail.
      </p>
      <div className="flex flex-col gap-1">
        <span className="text-[length:var(--text-xs)] font-medium text-[var(--color-ink-muted)]">Subject</span>
        <span data-testid="invite-subject" className="select-text break-words">
          {compose.subject}
        </span>
      </div>
      <div className="flex flex-col gap-1">
        <span className="text-[length:var(--text-xs)] font-medium text-[var(--color-ink-muted)]">Message</span>
        <pre
          ref={bodyRef}
          data-testid="invite-body"
          className="max-h-72 select-text overflow-auto whitespace-pre-wrap break-words rounded-[var(--radius-md)] border border-[var(--color-border)] bg-[var(--color-surface-subtle)] p-3 font-[family-name:var(--font-sans)] text-[length:var(--text-xs)] leading-relaxed"
        >
          {userContentSpans(compose.body, [compose.email, compose.link])}
        </pre>
      </div>
      <div className="flex flex-wrap items-center gap-3">
        <Button type="button" variant="secondary" data-testid="invite-copy" onClick={copy}>
          {status === "copied" ? "Copied" : "Copy message"}
        </Button>
        <span aria-live="polite" data-testid="invite-copy-state" className="text-[length:var(--text-xs)] text-[var(--color-ink-muted)]">
          {STATUS_LINE[status]}
        </span>
      </div>
    </div>
  );
}
